<template>
    <!-- Inline A2A Agent Card for a single agent (PLT-1615). Mounted lazily when the agent
         row is expanded, so it fetches its own card on mount. Shows what the agent
         advertises — notably capabilities.streaming. -->
    <div class="pb-3 pl-1">
        <BaseNotification v-if="error" theme="error" hide-dismiss>
            <template #content
                ><p class="body-small whitespace-pre-wrap break-words">{{ error }}</p></template
            >
        </BaseNotification>

        <div v-else-if="loading" class="body-small text-ink-muted">Loading card…</div>

        <div v-else-if="card" class="flex flex-col gap-2">
            <div class="flex items-center flex-wrap gap-2">
                <span v-if="card.version" class="text-xs text-ink-muted">v{{ card.version }}</span>
                <BaseChip size="2xs" class="text-ink-muted">
                    {{ card.capabilities?.streaming ? "Streaming ✓" : "No streaming" }}
                </BaseChip>
                <BaseChip v-if="card.capabilities?.pushNotifications" size="2xs" class="text-ink-muted"> Push ✓ </BaseChip>
            </div>
            <p v-if="card.description" class="body-small text-ink-muted">{{ card.description }}</p>
            <div v-if="skillNames" class="body-small text-ink-muted">Skills: {{ skillNames }}</div>
            <BaseButton theme="tertiary" size="sm" class="self-start" @click="showRaw = !showRaw">{{ showRaw ? "Hide" : "Show" }} raw card</BaseButton>
            <pre v-if="showRaw" class="text-xs text-ink bg-surface-sunken border border-border rounded-lg p-3 overflow-auto max-h-72">{{ pretty(card) }}</pre>
        </div>

        <div v-else class="body-small text-ink-muted">No card available for this agent.</div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { computed, ref } from "vue";

interface AgentCardSkill {
    id?: string;
    name?: string;
    description?: string;
}
interface AgentCard {
    name?: string;
    description?: string;
    version?: string;
    capabilities?: { streaming?: boolean; pushNotifications?: boolean };
    skills?: AgentCardSkill[];
    provider?: { organization?: string; url?: string };
}

const props = defineProps<{
    agentId: string;
    // Base agent-card endpoint; the agent id is appended as ?agent_id= (URL-encoded).
    cardUrl: string;
}>();

const card = ref<AgentCard | null>(null);
const error = ref("");
const loading = ref(false);
const showRaw = ref(false);

const skillNames = computed(() =>
    (card.value?.skills ?? [])
        .map((skill) => skill.name || skill.id)
        .filter(Boolean)
        .join(", "),
);

function pretty(value: unknown): string {
    return JSON.stringify(value, null, 2);
}

async function loadCard() {
    loading.value = true;
    error.value = "";
    try {
        const { data, response } = await useFetch(`${props.cardUrl}?agent_id=${encodeURIComponent(props.agentId)}`)
            .get()
            .json();
        if (response.value?.ok) {
            card.value = data.value?.card ?? null;
        } else {
            card.value = null;
            error.value = data.value?.error || "Failed to load agent card.";
        }
    } finally {
        loading.value = false;
    }
}

loadCard();
</script>
